import "./style.scss";

import * as React from "react";

import {
    Button,
    DialogContent,
    DialogTitle,
    DialogContentText,
    Dialog,
    Input,
    InputLabel,
    Typography,
} from "@mui/material";
import { Icon } from "../../../../common/component/icon/icon";
import { Article } from "../../../model/article";
import { t } from "i18next";
import { notificationModel } from "../../../../common/component/notifications/notification-model";
import { StockoutProductSimulationResult } from "./stockout-product-simulation-result";
import { Stockout } from "../../../model/stockout";
import { ArticleSelect } from "./article-select";

interface IProductStockout {
    contentPrefix: string;
    operationCode: string;
    batchId: number;
    deliveryOrderIds: number[];
    articles: Article[];
    reloadDeliveryOrderList: () => Promise<void>;
    closeStockoutDialog: () => void;
}

interface IProductStockoutState {
    selectedProduct: Article | null;
    missingQuantity: string | null;
    affectedDeliveryOrdersCount: number | null;
    calculatedQuantity: number | null;
}

export abstract class ProductStockout extends React.Component<IProductStockout, IProductStockoutState> {
    constructor(props: IProductStockout) {
        super(props);

        this.state = {
            selectedProduct: null,
            missingQuantity: null,
            affectedDeliveryOrdersCount: null,
            calculatedQuantity: null,
        };
    }

    public componentWillUnmount() {
        this.setState({
            selectedProduct: null,
            missingQuantity: null,
            affectedDeliveryOrdersCount: null,
            calculatedQuantity: null,
        });
    }

    public render() {
        return <Dialog
            onClose={() => this.onRequestClose()}
            open={true}
            data-testid="action-stockout-product"
            className={"stockout-popup-dialog"}
            PaperProps={{style: {overflowY: `visible`}}}
        >
            <DialogTitle>{t(`${this.props.contentPrefix}.title`)}</DialogTitle>
            <div className="close">
                <Icon icon="close" onClick={() => this.onRequestClose()} color="primary" />
            </div>
            <DialogContent className={"stockout-popup-content"}>
                { !this.isSimulationPrepared && this.renderSimulationForm() }
                { this.isSimulationPrepared && this.renderConfirmationBox() }
            </DialogContent>
        </Dialog>;
    }

    private renderSimulationForm() {
        return <div>
            <DialogContentText className={"stockout-popup-description"}>
                {t(`${this.props.contentPrefix}.selectProductDescription`)}
            </DialogContentText>
            <div>
                <div className="stockout-form-item" data-testid={"stockout-product-select"}>
                    <ArticleSelect selectedItem={this.state.selectedProduct}
                                   list={this.props.articles}
                                   onSelect={value => this.selectProduct(value)}
                                   label={t(`components.productSelector.label`)}
                                   propertyKey={"stockout-product-selector"}
                                   placeholder={t(`components.productSelector.placeholder`)}
                                   noOptionsMessage={t(`components.productSelector.noResults`)} />
                </div>
                <div className="stockout-form-item">
                    <InputLabel htmlFor="StockoutProductQty" className={"input-label"}>
                        {t("components.productStockout.quantity")}
                    </InputLabel>
                    <Input
                        id="StockoutProductQty"
                        onChange={e => this.setState({missingQuantity: e.target.value})}
                        className={"quantity-selector"}
                        disabled={!this.hasSelectedProduct}
                    />
                    { this.hasSelectedProduct ?
                        <Typography className="available-quantity" component="span">
                                    of { this.state.selectedProduct?.availableQuantity }
                                </Typography>
                        : undefined}
                </div>
            </div>
            <div className="input-submit-container">
                <Button variant="contained"
                        color={"inherit"}
                        data-testid="stockout-dialog-cancel"
                        onClick={() => this.onRequestClose()}
                        key="cancel-btn">{t(`components.popin.cancel`)}</Button>
                <Button
                    color="primary"
                    variant="contained"
                    type="submit"
                    data-testid="stockout-dialog-submit"
                    autoFocus key="submit-btn"
                    disabled={!this.isStockoutFormCompleted}
                    onClick={() => this.simulateStockoutExecution()}>{t(`components.popin.ok`)}</Button>
            </div>
        </div>;
    }

    private renderConfirmationBox() {
        return <div>
            <DialogContentText className={"stockout-popup-description"}>
                {t(`${this.props.contentPrefix}.confirmStockoutProduct`)}
            </DialogContentText>
            <div className="stockout-form-item">
                <Typography className="simulation-label" component="span">
                    {t("components.productStockout.product")}
                </Typography>
                <Typography className="simulation-value" component="span">
                    {this.state.selectedProduct!.name}
                </Typography>
            </div>
            <div className="stockout-form-item">
                <Typography className="simulation-label" component="span">
                    {t("components.productStockout.affectedQuantity")}
                </Typography>
                <Typography className="simulation-value" data-testid="simulation-calculated-qty" component="span">
                    {this.state.calculatedQuantity}
                </Typography>
            </div>
            <div className="stockout-form-item">
                <Typography className="simulation-label" component="span">
                    {t("components.productStockout.affectedDeliveryOrders")}
                </Typography>
                <Typography className="simulation-value" data-testid="simulation-affected-dos" component="span">
                    {this.state.affectedDeliveryOrdersCount}
                </Typography>
            </div>
            <div className="input-submit-container">
                <Button variant="contained"
                        data-testid="stockout-dialog-cancel"
                        onClick={() => this.onRequestClose()}
                        key="cancel-btn">{t(`components.popin.cancel`)}</Button>
                <Button
                    color="primary"
                    variant="contained"
                    type="submit"
                    data-testid="stockout-dialog-submit"
                    autoFocus key="submit-btn"
                    onClick={() => this.stockoutActionExecution()}>{t(`components.popin.ok`)}</Button>
            </div>
        </div>;
    }

    private onRequestClose() {
        this.props.closeStockoutDialog();
    }

    private get isSimulationPrepared() {
        return this.state.selectedProduct && this.state.affectedDeliveryOrdersCount;
    }

    private get hasSelectedProduct() {
        return this.state.selectedProduct !== null;
    }

    private get isMissingQuantityValid() {
        return this.state.missingQuantity !== null
            && !isNaN(Number(this.state.missingQuantity))
            && Number(this.state.missingQuantity) > 0;
    }

    private get isStockoutFormCompleted() {
        return this.state.selectedProduct && this.state.missingQuantity;
    }

    private async simulateStockoutExecution() {
        if (!this.hasSelectedProduct) {
            notificationModel.addErrorMessage(t(`${this.props.contentPrefix}.invalidProduct`));
            return;
        }

        const missingQuantityNumber = Number(this.state.missingQuantity);

        if (!this.isMissingQuantityValid) {
            notificationModel.addErrorMessage(t(`${this.props.contentPrefix}.invalidStockoutQuantity`, {
                maxQuantity: this.state.selectedProduct!.availableQuantity,
            }));
            return;
        }

        if (missingQuantityNumber > this.state.selectedProduct!.availableQuantity) {
            notificationModel.addErrorMessage(t(`${this.props.contentPrefix}.stockoutQuantityTooBig`, {
                maxQuantity: this.state.selectedProduct!.availableQuantity,
            }));
            return;
        }

        const result = await this.simulateAction(this.props.deliveryOrderIds, this.state.selectedProduct!, missingQuantityNumber);

        this.setState({ affectedDeliveryOrdersCount: result.affectedDeliveryOrders, calculatedQuantity: missingQuantityNumber });
    }

    private async stockoutActionExecution() {
        if (!this.isSimulationPrepared) {
            throw new Error("Invalid argument exception");
        }

        const stockoutList = await this.executeAction(this.props.deliveryOrderIds, this.state.selectedProduct!, Number(this.state.missingQuantity));

        notificationModel.addSuccessMessage(
            t(`${this.props.contentPrefix}.success`, {stockoutCount: stockoutList.length}),
        );

        this.onRequestClose();
        await this.props.reloadDeliveryOrderList();
    }

    private selectProduct(article: Article | null) {
        this.setState({ selectedProduct: article });
    }

    public abstract executeAction(deliveryOrderIds: number[], article: Article, quantity: number): Promise<Stockout[]>;
    public abstract simulateAction(deliveryOrderIds: number[], article: Article, quantity: number): Promise<StockoutProductSimulationResult>;
}
